// src/app/app.component.ts
import { Component, HostListener, OnInit, NgZone, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { AuthService } from '../app/guards/service/auth.service';
import { SideNavComponent } from '../app/private/side-nav/side-nav.component';
import { TopNavComponent } from '../app/private/top-nav/top-nav.component';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs/operators';
import { LayoutService } from './layout/layout.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  imports: [RouterOutlet, SideNavComponent, TopNavComponent, CommonModule],
  standalone: true,
})
export class AppComponent implements OnInit {
  private _navReady = false;

  constructor(
    public authService: AuthService,
    private router: Router,
    public layout: LayoutService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  get navReady(): boolean {
    return this._navReady;
  }

  get sideNavVisible(): boolean {
    return (
      this.authService.isLoggedIn() &&
      this.layout.showLayout &&
      this.layout.isSidebarVisible &&
      (this.layout.isMobileView ? this.layout.isSidebarOpen : true) &&
      this.navReady
    );
  }

  ngOnInit(): void {
    this.zone.run(() => {
      this.checkViewport();
      this.updateBodyClass();
    });

    // Recompute when token/space changes
    this.authService.authChanged$.subscribe(() => {
      this.computeNavReadiness(this.router.url);
      this.cdr.detectChanges();
    });

    // Recompute on every navigation
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((e: NavigationEnd) => {
      const url = e.urlAfterRedirects;
      this.zone.run(() => {
        setTimeout(() => {
          this.layout.showLayout = !this.isPublicSlugRoute(url);
          this.layout.isSidebarVisible = !this.shouldHideSidebar(url);
          this.computeNavReadiness(url);
          this.checkViewport();
          this.updateBodyClass();
          this.cdr.detectChanges();
        }, 30);
      });
    });

    this.computeNavReadiness(this.router.url);
  }

  private computeNavReadiness(url: string): void {
    const [path] = url.split('?');
    const seg = (path.startsWith('/') ? path.slice(1) : path).split('/').filter(Boolean);
    const routeSlug = seg[0] || '';
    const routeCid = Number(seg[1] || 0) || 0;

    if (!routeSlug || !routeCid) {
      this._navReady = this.authService.isLoggedIn();
      return;
    }

    const lsSpace = Number(localStorage.getItem('spaceID') || 0) || 0;
    this._navReady = this.authService.isLoggedIn() && lsSpace > 0;
  }

  // ... keep the rest of your methods unchanged (isPublicSlugRoute, shouldHideSidebar, etc.)

  @HostListener('window:resize') onResize(): void {
    this.zone.run(() => {
      this.checkViewport();
      this.cdr.detectChanges();
    });
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent): void {
    if (this.layout.isMobileView && this.layout.isSidebarOpen) {
      const target = event.target as HTMLElement;
      if (!target.closest('.side-nav') && !target.closest('.sidebar-toggle')) {
        this.closeSidebar();
      }
    }
  }

  checkViewport(): void {
    this.layout.isMobileView = window.innerWidth < 768;
    this.layout.isSidebarOpen = !this.layout.isMobileView && this.layout.showLayout;
    this.updateBodyScroll();
  }

  toggleSidebar(): void {
    this.layout.isSidebarOpen = !this.layout.isSidebarOpen;
    this.updateBodyScroll();
  }

  closeSidebar(): void {
    this.layout.isSidebarOpen = false;
    this.updateBodyScroll();
  }

  updateBodyScroll(): void {
    document.body.style.overflow =
      this.layout.isMobileView && this.layout.isSidebarOpen ? 'hidden' : '';
  }

  updateBodyClass(): void {
    document.body.classList.toggle('layout-visible', this.layout.showLayout);
  }

 isPublicSlugRoute(url: string): boolean {
  const [path] = url.split('?');
  const cleanPath = path.startsWith('/')
    ? path.slice(1)
    : path;

  const segments = cleanPath
    .split('/')
    .filter(Boolean);

  if (segments.length === 0) {
    return false;
  }

  const [
    first,
    second,
    third,
    fourth,
  ] = segments;

  const reserved = [
    'login',
    'home',
    'profile',
    'registration',
    'forgot-password',
    'reset-password',
    'community-users',
  ];

  const isPublicJobApplicantsPreview =
    segments.length === 3 &&
    first === 'public' &&
    second === 'job-applicants' &&
    !!third;

  if (isPublicJobApplicantsPreview) {
    return true;
  }

  if (
    first === 'login' ||
    first === 'forgot-password'
  ) {
    return true;
  }

  if (
    first === 'registration' &&
    segments.length >= 2
  ) {
    return true;
  }

  if (
    first === 'reset-password' &&
    segments.length >= 2
  ) {
    return true;
  }

  if (
    segments.length === 3 &&
    third === 'login'
  ) {
    return true;
  }

  if (
    segments.length === 3 &&
    third === 'forgot-password'
  ) {
    return true;
  }

  if (
    segments.length === 4 &&
    third === 'reset-password' &&
    fourth
  ) {
    return true;
  }

  if (
    segments.length === 4 &&
    third === 'registration' &&
    fourth
  ) {
    return true;
  }

  if (
    segments.length === 4 &&
    third === 'post'
  ) {
    return true;
  }

  return (
    segments.length === 1 &&
    !reserved.includes(first)
  );
}

  shouldHideSidebar(url: string): boolean {
    const [path] = url.split('?');
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    const segments = cleanPath.split('/').filter(Boolean);

    const isProfilePage = segments.length >= 3 && segments[2] === 'profile';
    const isCommunityUsersPage = segments.length >= 3 && segments[2] === 'community-users';
    const isApplicantsPage = segments.length >= 4 && segments[3] === 'applicants';
    const isPublicPostPreview = segments.length === 4 && segments[2] === 'post';

    return isProfilePage || isCommunityUsersPage || isApplicantsPage || isPublicPostPreview;
  }
}
